import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaRedo,
  FaSearch,
  FaShieldAlt
} from "react-icons/fa";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar/sidebar";
import api, { API_BASE_URL } from "../../utils/api";
import "./AuditLogs.css";

const today = () => new Date().toISOString().slice(0, 10);

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const statusLabel = (statusCode) => {
  if (!statusCode) return "Unknown";
  if (statusCode >= 200 && statusCode < 400) return "Success";
  return "Failed";
};

const AuditLogs = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1
  });
  const [filters, setFilters] = useState({
    search: "",
    method: "",
    status: "",
    from: "",
    to: today()
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      page: String(pagination.page),
      limit: String(pagination.limit)
    });

    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });

    return params.toString();
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    const loadLogs = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await api.get(`${API_BASE_URL}/api/audit-logs?${queryString}`);
        setLogs(res.data?.logs || []);
        setPagination((current) => ({
          ...current,
          ...(res.data?.pagination || {})
        }));
      } catch (err) {
        console.error("Error loading audit logs:", err.response?.data || err);
        setError(err.response?.data?.error || "Failed to load audit logs");
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [queryString]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPagination((current) => ({ ...current, page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      method: "",
      status: "",
      from: "",
      to: today()
    });
    setPagination((current) => ({ ...current, page: 1 }));
  };

  const goToPage = (nextPage) => {
    setPagination((current) => ({
      ...current,
      page: Math.min(Math.max(nextPage, 1), current.totalPages || 1)
    }));
  };

  return (
    <div className="audit-page-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      <div className={`audit-page-content ${collapsed ? "collapsed" : ""}`}>
        <div className="audit-sticky-header">
          <Header />
        </div>

        <div className="audit-scroll-area">
          <section className="audit-shell">
            <div className="audit-heading">
              <div>
                <span className="audit-eyebrow">
                  <FaShieldAlt /> System Settings / Employees
                </span>
                <h2>Audit Logs</h2>
                <p>Review user activity across CRM actions, protected by admin and RBAC access.</p>
              </div>
              <button type="button" className="audit-reset-btn" onClick={resetFilters}>
                <FaRedo /> Reset
              </button>
            </div>

            <div className="audit-filter-bar">
              <label className="audit-search">
                <FaSearch />
                <input
                  value={filters.search}
                  onChange={(e) => updateFilter("search", e.target.value)}
                  placeholder="Search user, employee ID, path, or action"
                />
              </label>

              <select value={filters.method} onChange={(e) => updateFilter("method", e.target.value)}>
                <option value="">All Methods</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>

              <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)}>
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>

              <input
                type="date"
                value={filters.from}
                onChange={(e) => updateFilter("from", e.target.value)}
              />
              <input
                type="date"
                value={filters.to}
                onChange={(e) => updateFilter("to", e.target.value)}
              />
            </div>

            {error && <div className="audit-error">{error}</div>}

            <div className="audit-table-wrap">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="audit-empty">Loading audit logs...</td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="audit-empty">No audit logs found.</td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log._id}>
                        <td>{formatDateTime(log.createdAt)}</td>
                        <td>
                          <strong>{log.actor?.name || "Unknown User"}</strong>
                          <span>{log.actor?.employeeId || log.actor?.email || log.actor?.roleName || "-"}</span>
                        </td>
                        <td>
                          <strong>{log.method}</strong>
                          <span>{log.action}</span>
                        </td>
                        <td title={log.path}>{log.path}</td>
                        <td>
                          <span className={`audit-status ${statusLabel(log.statusCode).toLowerCase()}`}>
                            {statusLabel(log.statusCode)} {log.statusCode || ""}
                          </span>
                        </td>
                        <td>{log.ipAddress || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="audit-pagination">
              <span>
                Showing {logs.length} of {pagination.total} logs
              </span>
              <div>
                <button type="button" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1}>
                  <FaChevronLeft />
                </button>
                <strong>
                  {pagination.page} / {pagination.totalPages || 1}
                </strong>
                <button
                  type="button"
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page >= (pagination.totalPages || 1)}
                >
                  <FaChevronRight />
                </button>
              </div>
              <select
                value={pagination.limit}
                onChange={(e) =>
                  setPagination((current) => ({
                    ...current,
                    page: 1,
                    limit: Number(e.target.value)
                  }))
                }
              >
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
